import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import RegressionBannerImage from "../../../../assets/services/asic-design/asicDesign3.png";
import ProfileImage from "../../../../assets/blogpages/person.png";
import "./RegressionTestingCoverage.css";
import { AnimatePresence, motion } from "framer-motion";

// Simple highlight component: wraps matches in <mark>
const Highlight = ({ text = "", query = "" }) => {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i}>{part}</mark>
      : part
  );
};

const RegressionTestingCoverage = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showThankYou, setShowThankYou] = useState(false);
  const [replyVisible, setReplyVisible] = useState({});

  const closeOverlay = () => setShowThankYou(false);
  const toggleReply = id => setReplyVisible(prev => ({ ...prev, [id]: !prev[id] }));

  // refs for scrolling
  const sectionRefs = {
    intro: useRef(null),
    fundamentals: useRef(null),
    suiteDesign: useRef(null),
    coverageAnalysis: useRef(null),
    automation: useRef(null),
    testData: useRef(null),
    metrics: useRef(null),
    toolIntegration: useRef(null),
    challenges: useRef(null),
    trends: useRef(null),
    comments: useRef(null),
  };
  const scrollTo = ref =>
    ref.current && ref.current.scrollIntoView({ behavior: "smooth", block: "center" });

  // search + jump to first match
  useEffect(() => {
    if (!searchTerm) return;
    const firstMark = document.querySelector("mark");
    firstMark?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [searchTerm]);

  // fade‐in/out on scroll
  useEffect(() => {
    const io = new IntersectionObserver(
      entries =>
        entries.forEach(e =>
          e.isIntersecting
            ? e.target.classList.add("visible")
            : e.target.classList.remove("visible")
        ),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-section").forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  // auto‐dismiss popup
  useEffect(() => {
    if (showThankYou) {
      const t = setTimeout(() => setShowThankYou(false), 2000);
      return () => clearTimeout(t);
    }
  }, [showThankYou]);

  const handleSearch = () => setSearchTerm(searchInput.trim());
  const handleCommentSubmit = e => {
    e.preventDefault();
    setShowThankYou(true);
    e.target.reset();
  };

  return (
    <div className="blog-post-page regression-testing-coverage">
      {/* Banner */}
      <div className="blog-banner fade-section">
        <div className="banner-overlay">
          <h1>Regression Testing & Coverage</h1>
          <div className="breadcrumb">
            <Link to="/services/asic-verification">Services</Link> | Regression Testing
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      <div className="mobile-nav fade-section">
        <div className="search-box">
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            type="text"
            placeholder="Search keyword..."
            className="search-input"
          />
          <button onClick={handleSearch} className="search-button">
            Search
          </button>
        </div>
        <div className="table-of-contents">
          <h4>In This Article</h4>
          <ul>
            {Object.entries(sectionRefs).map(([key, ref]) => (
              <li key={key}>
                <button onClick={() => scrollTo(ref)}>
                  {{
                    intro: "Introduction",
                    fundamentals: "Fundamentals",
                    suiteDesign: "Designing Regression Suites",
                    coverageAnalysis: "Coverage Analysis",
                    automation: "Automating Tests",
                    testData: "Test Data & Environments",
                    metrics: "Metrics & Reporting",
                    toolIntegration: "Tool Integration & CI/CD",
                    challenges: "Challenges & Best Practices",
                    trends: "Future Trends",
                    comments: "Comments",
                  }[key]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Content */}
      <div className="blog-content-container">
        <div className="blog-left-column">
          <img
            className="post-main-image fade-section"
            src={RegressionBannerImage}
            alt="Regression Testing Banner"
          />
          <div className="post-info-row fade-section">
            <span className="post-author">By Jane Doe</span>
            <span className="post-category">Category: ASIC Verification</span>
            <button className="comment-link" onClick={() => scrollTo(sectionRefs.comments)}>
              0 Comments
            </button>
          </div>

          {/* 1. Introduction */}
          <h2 ref={sectionRefs.intro} className="fade-section">Introduction</h2>
          <p className="fade-section">
            <Highlight
              text="Regression testing ensures that previously validated functionality continues to work after changes, patches, or enhancements in ASIC designs."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Coverage analysis in regression helps quantify how much of the design and its assertions are exercised, guiding test improvements."
              query={searchTerm}
            />
          </p>

          {/* 2. Fundamentals */}
          <h2 ref={sectionRefs.fundamentals} className="fade-section">Fundamentals of Regression Testing</h2>
          <p className="fade-section">
            <Highlight
              text="At its core, regression testing re-runs a suite of existing tests—simulation vectors, formal checks, and coverage measurements—after design modifications."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Key goals include detecting unintended side-effects, verifying bug fixes, and maintaining confidence in the overall design correctness."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Effective regression requires clear test baselines, controlled environments, and efficient reporting to avoid increasing cycle times."
              query={searchTerm}
            />
          </p>

          {/* 3. Designing Regression Suites */}
          <h2 ref={sectionRefs.suiteDesign} className="fade-section">Designing Effective Regression Suites</h2>
          <p className="fade-section">
            <Highlight
              text="Modularize tests by feature blocks or IP, enabling selective runs on impacted areas rather than full-chip regressions every iteration."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Employ prioritized test execution—smoke tests first, followed by medium-priority checks, then deep verification—balancing coverage with turnaround time."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Leverage parameterized testbenches to cover multiple configurations with fewer scripts, reducing maintenance and improving scalability."
              query={searchTerm}
            />
          </p>

          {/* 4. Coverage Analysis */}
          <h2 ref={sectionRefs.coverageAnalysis} className="fade-section">Coverage Analysis in Regression</h2>
          <p className="fade-section">
            <Highlight
              text="Functional coverage points, code (statement/branch) coverage, and assertion coverage together provide a multidimensional view of test completeness."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Identifying coverage holes uncovers untested corner cases—critical for corner-case detection in corner-driven ASIC logic."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Coverage trend reports track progress over time, highlighting regressions when lower coverage appears after code changes."
              query={searchTerm}
            />
          </p>

          {/* 5. Automating Tests */}
          <h2 ref={sectionRefs.automation} className="fade-section">Automating Regression Tests</h2>
          <p className="fade-section">
            <Highlight
              text="Integrate regression scripts into CI pipelines (Jenkins, GitLab CI) to trigger on every commit, merge request, or nightly build."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Use containerized environments (Docker) or virtualization to provide consistent simulation and coverage tool versions across teams."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Generate HTML/XML dashboards automatically upon completion, and send notifications on failures or coverage drops."
              query={searchTerm}
            />
          </p>

          {/* 6. Test Data & Environments */}
          <h2 ref={sectionRefs.testData} className="fade-section">Managing Test Data & Environments</h2>
          <p className="fade-section">
            <Highlight
              text="Maintain versioned stimulus libraries and regression configuration files in source control to ensure repeatability."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Automate environment setup scripts to provision licenses, tool chains, and shared storage for large trace files."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Implement sandboxed workspaces per branch or feature to avoid cross-pollution of simulation artifacts."
              query={searchTerm}
            />
          </p>

          {/* 7. Metrics & Reporting */}
          <h2 ref={sectionRefs.metrics} className="fade-section">Metrics & Reporting</h2>
          <p className="fade-section">
            <Highlight
              text="Key metrics include total tests run, pass/fail rates, coverage percentage, and time per regression."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Track and visualize historical data: regression duration, flakiness rates, and coverage trends to identify bottlenecks."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Feed these insights back into test planning to retire outdated tests and focus on high-value scenarios."
              query={searchTerm}
            />
          </p>

          {/* 8. Tool Integration & CI/CD */}
          <h2 ref={sectionRefs.toolIntegration} className="fade-section">Tool Integration & CI/CD</h2>
          <p className="fade-section">
            <Highlight
              text="Connect EDA simulators, formal engines, coverage tools, and lint checks into a unified pipeline for single-click regressions."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Use scriptable flows (Python/Tcl) to dynamically generate run lists and aggregate coverage across mixed-language environments."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Parallelize tests on cloud or on-prem clusters to reduce total regression time while preserving result accuracy."
              query={searchTerm}
            />
          </p>

          {/* 9. Challenges & Best Practices */}
          <h2 ref={sectionRefs.challenges} className="fade-section">Challenges & Best Practices</h2>
          <p className="fade-section">
            <Highlight
              text="Balancing test breadth vs depth: overly broad suites run slowly, while too narrow suites miss regressions."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Combat flakiness by isolating non-deterministic tests and fixing root causes—unstable regressions erode trust."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Continuously review and prune the suite: retire obsolete tests, refactor redundant cases, and add new tests for feature growth."
              query={searchTerm}
            />
          </p>

          {/* 10. Future Trends */}
          <h2 ref={sectionRefs.trends} className="fade-section">Future Trends</h2>
          <p className="fade-section">
            <Highlight
              text="AI-powered test generation will suggest new regression vectors based on code diffs and historical failures."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Self-healing pipelines will automatically diagnose and rerun failed regressions, reducing manual intervention."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Unified platforms will offer end-to-end verification—from RTL to silicon—combining regression, formal, and emulation in one dashboard."
              query={searchTerm}
            />
          </p>

          {/* Comment Section */}
          <div className="comment-section" ref={sectionRefs.comments}>
            <h3>Leave a comment</h3>
            <div className="leave-comment fade-section">
              <p>Have questions or feedback? Fill out the form below!</p>
              <form onSubmit={handleCommentSubmit}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" placeholder="Name here..." required />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea placeholder="Message here…" required />
                </div>
                <button type="submit" className="send-message-btn">
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="blog-right-column fade-section">
          <div className="search-box">
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              type="text"
              placeholder="Search keyword..."
              className="search-input"
            />
            <button onClick={handleSearch} className="search-button">
              Search
            </button>
          </div>
          <div className="table-of-contents">
            <h4>In This Article</h4>
            <ul>
              {Object.entries(sectionRefs).map(([key, ref]) => (
                <li key={key}>
                  <button onClick={() => scrollTo(ref)}>
                    {{
                      intro: "Introduction",
                      fundamentals: "Fundamentals",
                      suiteDesign: "Designing Regression Suites",
                      coverageAnalysis: "Coverage Analysis",
                      automation: "Automating Tests",
                      testData: "Test Data & Environments",
                      metrics: "Metrics & Reporting",
                      toolIntegration: "Tool Integration & CI/CD",
                      challenges: "Challenges & Best Practices",
                      trends: "Future Trends",
                      comments: "Comments",
                    }[key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Thank You Popup */}
        <AnimatePresence>
          {showThankYou && (
            <motion.div
              className="thank-you-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeOverlay}
            >
              <motion.div
                className="thank-you-content"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                transition={{ type: "spring", stiffness: 300 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="checkmark-container">
                  <motion.svg
                    className="checkmark"
                    viewBox="0 0 52 52"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                  >
                    <circle cx="26" cy="26" r="25" stroke="#007bff" strokeWidth="2" />
                    <path
                      d="M16 27.5L23 34.5L36 19.5"
                      stroke="#007bff"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                </div>
                <h3>Thank you for the feedback!</h3>
                <p>We appreciate your comment.</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default RegressionTestingCoverage;